<template>
  <div class="col-12 pd-5">
    <div class="card mb-0 hr-workforce">
      <div class="hw-head">
        <div class="hw-title-wrap">
          <div class="hw-title-text">
            <h5 class="hw-title">{{ summary.name }}</h5>
            <span class="hw-subtitle">{{ onlyCommaFormat(summary.all.total) }} staff · {{ subtitleScope }}</span>
          </div>
        </div>
        <div class="hw-head-actions">
          <v-select
            v-if="!isBranchScoped"
            v-model="selectedBranch"
            :options="branchOptions"
            label="name"
            :reduce="option => option.key"
            :clearable="false"
            placeholder="Search branch"
            class="hw-branch-select"
          />
          <button type="button" class="btn btn-sm hw-export-btn" :disabled="summary.all.total === 0" @click="exportReport">
            <i class="fa-solid fa-download"></i> Export
          </button>
        </div>
      </div>
      <p v-if="summary.all.total === 0" class="hw-empty">No employees found.</p>
      <div v-else class="hw-body">
        <div class="hw-kpi-grid">
          <HrWorkforceKpiTile label="Total staff" :value="onlyCommaFormat(current.total)" color="#1e293b" :note="selectedBranchName" />
          <HrWorkforceKpiTile
            v-for="item in kpiCategoryItems"
            :key="item.key"
            :label="item.label"
            :value="onlyCommaFormat(item.value)"
            :color="item.color"
            :pill="percentLabel(item.value, current.total)"
            :pill-style="pctStyle(item)"
            note="of staff"
          />
          <HrWorkforceKpiTile
            v-if="hasData(tenureItems)"
            label="New joiners"
            :value="onlyCommaFormat(joinedThisYear)"
            :pill="percentLabel(joinedThisYear, current.total)"
            note="joined this year"
          />
        </div>
        <div class="hw-tabs">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            type="button"
            class="hw-tab-item"
            :class="{ active: currentTab === tab.key }"
            @click="activeTab = tab.key"
          >{{ tab.title }}</button>
        </div>
        <HrWorkforceOverviewTab v-if="currentTab === 'overview'" :current="current" :labels="labels" />
        <HrWorkforceOrganisationTab v-if="currentTab === 'organisation'" :current="current" :labels="labels" />
        <HrWorkforceAgeTab
          v-if="currentTab === 'age'"
          :current="current"
          :labels="labels"
          :branch-rows="branchRows"
          :selected-branch="selectedBranch"
          :selected-branch-name="selectedBranchName"
          :age-employees="summary.age_employees || {}"
          @select-branch="selectedBranch = $event"
        />
        <HrWorkforceBranchesTab
          v-if="currentTab === 'branches'"
          :summary="summary"
          :current="current"
          :labels="labels"
          :branch-rows="branchRows"
          :selected-branch="selectedBranch"
          :selected-branch-name="selectedBranchName"
          @select-branch="selectedBranch = $event"
        />
        <HrWorkforceDivisionTab v-if="currentTab === 'divisions'" :summary="summary" :current="current" :labels="labels" />
        <HrWorkforceProfileTab
          v-if="currentTab === 'profile'"
          :current="current"
          :labels="labels"
          :branch-rows="branchRows"
          :selected-branch="selectedBranch"
          :selected-branch-name="selectedBranchName"
          :attention-employees="summary.profile_employees || {}"
          @select-branch="selectedBranch = $event"
        />
        <HrWorkforceTableTab
          v-if="currentTab === 'table'"
          v-model:search="searchText"
          v-model:sortKey="sortKey"
          v-model:sortAsc="sortAsc"
          v-model:page="tablePage"
          :summary="summary"
          :labels="labels"
          :branch-rows="branchRows"
          :selected-branch="selectedBranch"
          @select-branch="selectedBranch = $event"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import figureFormatter from '@/services/utils/figureFormatter'
import { hasData, pctStyle, percentLabel, useHrWorkforceItems } from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/hrWorkforce'
import HrWorkforceKpiTile from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/HrWorkforceKpiTile.vue'
import HrWorkforceOverviewTab from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/HrWorkforceOverviewTab.vue'
import HrWorkforceOrganisationTab from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/HrWorkforceOrganisationTab.vue'
import HrWorkforceBranchesTab from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/HrWorkforceBranchesTab.vue'
import HrWorkforceDivisionTab from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/HrWorkforceDivisionTab.vue'
import HrWorkforceTableTab from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/HrWorkforceTableTab.vue'
import HrWorkforceProfileTab from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/HrWorkforceProfileTab.vue'
import HrWorkforceAgeTab from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/HrWorkforceAgeTab.vue'

const props = defineProps({
  statisticsItem: Object,
  page: Object,
  itemKey: String
})

const $route = useRoute()
const { onlyCommaFormat } = figureFormatter()

const summary = computed(() => props.statisticsItem[props.itemKey].data)
const selectedBranch = ref(summary.value.scoped_branch || 'all')
const activeTab = ref('overview')
const searchText = ref('')
const sortKey = ref('total')
const sortAsc = ref(false)
const tablePage = ref(1)

const isBranchScoped = computed(() => Boolean(summary.value.scoped_branch))
const labels = computed(() => summary.value.labels)
const current = computed(() => selectedBranch.value === 'all' ? summary.value.all : summary.value.branches[selectedBranch.value] || summary.value.all)
const branchRows = computed(() => Object.keys(summary.value.branches).map(key => ({
  key,
  name: labels.value.branches[key] || 'Not assigned',
  summary: summary.value.branches[key]
})))
const branchOptions = computed(() => [
  { key: 'all', name: 'All branches' },
  ...[...branchRows.value].sort((a, b) => a.name.localeCompare(b.name)).map(row => ({ key: row.key, name: row.name }))
])
const selectedBranchName = computed(() => selectedBranch.value === 'all' ? 'All branches' : labels.value.branches[selectedBranch.value] || 'Not assigned')
const subtitleScope = computed(() => isBranchScoped.value ? selectedBranchName.value : `${onlyCommaFormat(branchRows.value.length)} branches`)
const joinedThisYear = computed(() => current.value.joined_this_year || 0)

const { categoryItems, tenureItems, divisionItems, ageItems, visibleDonuts, visibleRankedLists } = useHrWorkforceItems(current, labels, summary)

const kpiCategoryItems = computed(() => hasData(categoryItems.value) ? categoryItems.value : [])

const tabs = computed(() => [
  { key: 'overview', title: 'Overview', visible: visibleDonuts.value.length > 0 },
  { key: 'organisation', title: 'Organisation', visible: visibleRankedLists.value.length > 0 },
  { key: 'age', title: 'Age Distribution', visible: hasData(ageItems.value) },
  { key: 'branches', title: 'Branches', visible: true },
  { key: 'divisions', title: 'District Fellowship', visible: hasData(divisionItems.value) },
  { key: 'profile', title: 'Profile Completion', visible: Boolean(summary.value.all.profile) },
  { key: 'table', title: 'Table', visible: true }
].filter(tab => tab.visible))
const currentTab = computed(() => tabs.value.some(tab => tab.key === activeTab.value) ? activeTab.value : tabs.value[0].key)

const exportReport = () => {
  const token = localStorage.getItem('token')
  const query = `?company_id=${$route.params.companyId}&module_id=${$route.params.moduleId}&branch=${selectedBranch.value}&_token=${token}`
  const link = document.createElement('a')
  link.setAttribute('href', `${process.env.VUE_APP_BASE_URL}/export/hr-payroll/hr-workforce${query}`)
  link.click()
}
</script>

<style scoped>
.hr-workforce {
  background: #f8fafc;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.06);
}
.hw-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 16px 20px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
}
.hw-title-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.hw-title-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.hw-title {
  margin: 0;
  font-weight: 700;
  color: #0f172a;
  font-size: 1.05rem;
  line-height: 1.3;
}
.hw-subtitle {
  color: #64748b;
  font-size: 0.78rem;
}
.hw-head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.hw-branch-select {
  width: 260px;
  max-width: 100%;
}
.hw-export-btn {
  height: 34px;
  padding: 0 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #0f766e;
  font-weight: 600;
  white-space: nowrap;
}
.hw-export-btn:hover {
  background: #f0fdfa;
  color: #0f766e;
}
.hw-branch-select :deep(.vs__dropdown-toggle) {
  min-height: 34px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}
.hw-branch-select :deep(.vs__selected) {
  font-size: 0.85rem;
  color: #1e293b;
}
.hw-tabs {
  display: flex;
  gap: 22px;
  flex-wrap: nowrap;
  overflow-x: auto;
  scrollbar-width: none;
  border-bottom: 1px solid #e2e8f0;
}
.hw-tab-item {
  cursor: pointer;
  padding: 8px 2px;
  border: 0;
  background: transparent;
  font-size: 0.9rem;
  font-weight: 600;
  color: #64748b;
  white-space: nowrap;
  position: relative;
}
.hw-tab-item:focus-visible {
  outline: 2px solid #0f766e;
  outline-offset: 2px;
}
.hw-tab-item:hover,
.hw-tab-item.active {
  color: #0f766e;
}
.hw-tab-item.active::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -1px;
  width: 100%;
  height: 2px;
  background: #0f766e;
}
.hw-empty {
  text-align: center;
  color: #475569;
  padding: 32px 0;
  margin: 0;
  background: #fff;
}
.hw-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
}
.hw-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 14px;
}
@media (max-width: 575px) {
  .hw-head {
    flex-direction: column;
    align-items: stretch;
  }
  .hw-head-actions {
    width: 100%;
  }
  .hw-branch-select {
    flex: 1;
    min-width: 0;
  }
  .hw-kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
